import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Tooltip, Chip, Alert, CircularProgress, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, TextField, Snackbar,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { rfqAPI, supplierOffersAPI } from '../../services/api';

const fmt = (v) => (v === null || v === undefined
  ? '—'
  : Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/**
 * Comparativa de la licitacion: equipos en filas, proveedores en columnas.
 *
 * Se pulsa una celda para adjudicar ese equipo a ese proveedor. Como un equipo
 * solo puede tener una adjudicacion, hacerlo se la quita a quien la tuviera, y
 * la aplicacion dice a quien en lugar de cambiarlo en silencio.
 */
export default function QuoteComparison({ projectId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [rfqCode, setRfqCode] = useState('');
  const [orderConfirm, setOrderConfirm] = useState(null);   // {offerId, detail}

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const resp = await rfqAPI.comparison(projectId, rfqCode || undefined);
      setData(resp.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido cargar la comparativa.');
    } finally {
      setLoading(false);
    }
  }, [projectId, rfqCode]);

  useEffect(() => { if (!collapsed) load(); }, [collapsed, load]);

  const run = async (fn) => {
    try {
      setError('');
      const resp = await fn();
      if (resp?.data?.message) setToast(resp.data.message);
      await load();
      if (onChanged) await onChanged();
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido completar la operacion.');
      return false;
    }
  };

  const cycleAward = (cell) => {
    if (!cell || cell.is_locked || cell.not_quoted) return;
    // sin decidir -> adjudicada -> descartada -> sin decidir
    const next = cell.is_awarded === true ? false : cell.is_awarded === false ? null : true;
    run(() => supplierOffersAPI.awardLine(cell.line_id, next));
  };

  const createOrder = async (offerId, force = false) => {
    try {
      setError('');
      const resp = await supplierOffersAPI.createOrder(offerId, force ? { force: true } : {});
      setToast(resp.data.message);
      setOrderConfirm(null);
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail || 'No se ha podido generar el pedido.';
      if (err.response?.status === 409 && detail.includes('sin adjudicar')) {
        setOrderConfirm({ offerId, detail });
      } else {
        setError(detail);
      }
    }
  };

  const cellSx = (cell) => {
    if (!cell) return { backgroundColor: '#fafafa' };
    if (cell.is_awarded === true) return { backgroundColor: '#c8e6c9', fontWeight: 'bold' };
    if (cell.is_awarded === false) return { opacity: 0.45, textDecoration: 'line-through' };
    if (cell.not_quoted) return { color: 'text.disabled' };
    if (cell.is_best) return { backgroundColor: '#e3f2fd' };
    return {};
  };

  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const extras = data?.extras || [];

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer', userSelect: 'none', flexWrap: 'wrap' }}
        onClick={() => setCollapsed((v) => !v)}
      >
        {collapsed ? <ExpandMore color="action" /> : <ExpandLess color="action" />}
        <Typography variant="h6">Comparativa de ofertas</Typography>
        {data && (
          <Chip size="small" color={columns.length ? 'primary' : 'default'}
            variant={columns.length ? 'filled' : 'outlined'}
            label={columns.length ? `${columns.length} proveedores` : 'vacia'}
            sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} />
        )}
        {data && data.unawarded_equipment?.length > 0 && (
          <Chip size="small" color="warning" icon={<WarningAmberIcon />}
            label={`${data.unawarded_equipment.length} sin adjudicar`} />
        )}
        {data && data.awarded_total > 0 && (
          <Paper sx={{ px: 2, py: 0.3, backgroundColor: '#c62828', color: '#fff' }}
            onClick={(e) => e.stopPropagation()}>
            <Typography variant="caption" sx={{ color: '#fff' }}>
              Adjudicado: <b>{fmt(data.awarded_total)} EUR</b>
            </Typography>
          </Paper>
        )}
      </Box>

      <Collapse in={!collapsed}>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : columns.length === 0 ? (
          <Alert severity="info">
            Todavia no hay ofertas con partidas. Marca equipos en la lista y pulsa
            &laquo;Pedir oferta&raquo; para empezar la licitacion.
          </Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
              {data.rfq_codes?.length > 1 && (
                <TextField select size="small" label="Licitacion" value={rfqCode}
                  onChange={(e) => setRfqCode(e.target.value)} sx={{ minWidth: 180 }}>
                  <MenuItem value=""><em>Todas</em></MenuItem>
                  {data.rfq_codes.map((code) => (
                    <MenuItem key={code} value={code}>{code}</MenuItem>
                  ))}
                </TextField>
              )}
              <Typography variant="caption" color="textSecondary">
                Pulsa una celda para adjudicar ese equipo a ese proveedor; otra vez para descartarla,
                y una tercera para dejarla sin decidir.
              </Typography>
            </Box>

            {data.unawarded_equipment?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Sin proveedor: {data.unawarded_equipment.join(', ')}
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#c62828' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold', minWidth: 190 }}>Equipo</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="right">Estimado</TableCell>
                    {columns.map((col) => (
                      <TableCell key={col.offer_id} align="right"
                        sx={{ color: '#fff', fontWeight: 'bold', minWidth: 150 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.3 }}>
                          <span>{col.supplier_name}</span>
                          <Typography variant="caption" sx={{ color: '#ffcdd2' }}>
                            cotiza {col.coverage_quoted}/{col.coverage_asked}
                            {(col.revision || 1) > 1 ? ` · rev.${col.revision}` : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.equipment_id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {row.tag ? <b>{row.tag}</b> : null} {row.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="textSecondary">
                          {fmt(row.estimated_cost)}
                        </Typography>
                      </TableCell>
                      {columns.map((col) => {
                        const cell = row.cells[String(col.offer_id)];
                        return (
                          <TableCell key={col.offer_id} align="right"
                            onClick={() => cycleAward(cell)}
                            sx={{
                              cursor: cell && !cell.is_locked && !cell.not_quoted ? 'pointer' : 'default',
                              ...cellSx(cell),
                            }}
                          >
                            {!cell ? '' : cell.not_quoted ? (
                              <Tooltip title="El proveedor no cotiza esta partida">
                                <span>— no cotiza</span>
                              </Tooltip>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                {cell.spec_changed && (
                                  <Tooltip title="El equipo ha cambiado desde que se pidio esta oferta">
                                    <WarningAmberIcon fontSize="inherit" color="error" />
                                  </Tooltip>
                                )}
                                {cell.group_label && (
                                  <Tooltip title={`Precio conjunto: ${cell.group_label}`}>
                                    <Chip size="small" label="conj." variant="outlined"
                                      sx={{ height: 16, fontSize: '0.58rem' }} />
                                  </Tooltip>
                                )}
                                {cell.is_awarded === true && <CheckCircleIcon fontSize="inherit" color="success" />}
                                {cell.is_locked && (
                                  <Tooltip title="Ya esta en un pedido">
                                    <LockIcon fontSize="inherit" color="action" />
                                  </Tooltip>
                                )}
                                <span>{fmt(cell.amount)}</span>
                              </Box>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}

                  {extras.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#fff8e1' }}>
                      <TableCell colSpan={2}>
                        <Typography variant="caption" fontWeight="bold">
                          Partidas fuera del listado de equipos
                        </Typography>
                      </TableCell>
                      {columns.map((col) => (
                        <TableCell key={col.offer_id} align="right">
                          {extras.filter((e) => e.offer_id === col.offer_id).map((e) => (
                            <Tooltip key={e.line_id} title={e.description || ''}>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                {e.description}: {fmt(e.amount)}
                              </Typography>
                            </Tooltip>
                          ))}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}

                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell colSpan={2}><b>Total comparable / con extras</b></TableCell>
                    {columns.map((col) => (
                      <TableCell key={col.offer_id} align="right">
                        <Typography variant="body2" fontWeight="bold">{fmt(col.equipment_total)}</Typography>
                        <Typography variant="caption" color="textSecondary">{fmt(col.lines_total)}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={2}><b>Adjudicado</b></TableCell>
                    {columns.map((col) => (
                      <TableCell key={col.offer_id} align="right">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight="bold" color={col.awarded_count ? 'success.main' : 'text.disabled'}>
                            {col.awarded_count ? `${fmt(col.awarded_total)} (${col.awarded_count})` : '—'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Button size="small" onClick={() => run(() => supplierOffersAPI.awardAll(col.offer_id))}>
                              Adjudicar toda
                            </Button>
                            {col.awarded_count > 0 && (
                              <Button size="small" variant="contained" startIcon={<ShoppingCartIcon />}
                                onClick={() => createOrder(col.offer_id)}>
                                Pedido
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Collapse>

      <Dialog open={!!orderConfirm} onClose={() => setOrderConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Quedan equipos sin adjudicar</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>{orderConfirm?.detail}</Alert>
          <Typography variant="body2">
            El pedido se generara solo con las partidas adjudicadas a este proveedor. El resto
            seguira pendiente de decidir en la comparativa.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderConfirm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => createOrder(orderConfirm.offerId, true)}>
            Generar igualmente
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast('')}
        message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
